// Đăng ký push OneSignal bằng Web SDK v16 (không cần thêm package).
// Chỉ kích hoạt khi có VITE_ONE_SIGNAL_APP_ID. An toàn khi tắt: mọi hàm no-op.
import { oneSignalApi } from "api/oneSignal.api";
import { getToken } from "./auth";

declare global {
  interface Window {
    OneSignalDeferred?: Array<(oneSignal: any) => void>;
  }
}

const APP_ID = import.meta.env.VITE_ONE_SIGNAL_APP_ID as string | undefined;

let initialized = false;

export const isOneSignalEnabled = () => !!APP_ID;

const pushDeferred = (fn: (oneSignal: any) => void) => {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(fn);
};

// Gửi playerId lên backend. Chưa login thì chỉ lưu local, bell sẽ flush sau login.
const postPlayerId = async (playerId: string) => {
  localStorage.setItem("oneSignalId", playerId);
  if (!getToken()) {
    console.info("[OneSignal] chưa đăng nhập, giữ playerId chờ đồng bộ sau login");
    return;
  }
  await oneSignalApi.sub({ oneSignalId: playerId });
  console.info("[OneSignal] đã đồng bộ playerId");
};

// Flush playerId còn nợ (đăng ký push trước khi login) — gọi khi đã có token.
export const syncPendingPlayerId = async () => {
  const playerId = localStorage.getItem("oneSignalId");
  if (!playerId || !getToken()) return;
  try {
    await oneSignalApi.sub({ oneSignalId: playerId });
    console.info("[OneSignal] đã đồng bộ playerId");
  } catch (error) {
    console.warn("[OneSignal] đồng bộ playerId thất bại", error);
  }
};

export const initOneSignal = () => {
  console.log('call initOneSignal');

  if (!isOneSignalEnabled() || initialized) return;
  initialized = true;

  pushDeferred(async (oneSignal: any) => {
    try {
      await oneSignal.init({ appId: APP_ID, allowLocalhostAsSecureOrigin: false });
    } catch (error) {
      console.warn("[OneSignal] init thất bại", error);
      return;
    }

    const push = oneSignal?.User?.PushSubscription;
    if (!push || typeof push.addEventListener !== "function") {
      console.warn("[OneSignal] không tìm thấy pushSubscription");
      return;
    }

    // Dùng event.current theo docs v16: id có thể về sau token qua nhiều lần change,
    // đọc lại push.id ở đây sẽ trượt timing khi vừa bấm subscribe.
    push.addEventListener("change", (event: any) => {
      const current = event?.current || {};
      console.info("[OneSignal] subscription change", current);
      if (current.optedIn && current.id) {
        postPlayerId(current.id).catch((error) =>
          console.warn("[OneSignal] gửi playerId thất bại", error),
        );
      } else if (!current.optedIn) {
        unsubscribeOneSignal().catch(() => undefined);
      }
    });

    // Đã subscribe từ trước (quay lại sau khi từng cho phép)
    try {
      if (push.optedIn && push.id) {
        await postPlayerId(push.id);
      }
    } catch (error) {
      console.warn("[OneSignal] gửi playerId thất bại", error);
    }
  });
};

export const unsubscribeOneSignal = async () => {
  const id = localStorage.getItem("oneSignalId");
  if (!id) return;
  try {
    if (getToken()) {
      await oneSignalApi.unSub({ oneSignalId: id });
    }
  } finally {
    localStorage.removeItem("oneSignalId");
  }
};
